import {
    BloombergMarketDataRequest,
    mapToBloombergRequest,
    MarketDataRequest,
    requestUid,
} from 'marketData/MarketDataRequest';
import {
    buildLogger,
    Logger,
    LoggingService,
} from '@instinet-analytics/core-lib';
import { MarketDataService } from 'marketData/MarketDataService';
import { Store } from 'redux';
import { SymbolsService } from 'symbols/SymbolsService';

export interface WidgetMarketDataRequestManagerOptions {
    loggingService: LoggingService;
    marketDataService: MarketDataService;
    symbolsService: SymbolsService;
}

export class WidgetMarketDataRequestManager {
    private logger: Logger;
    private marketDataService: MarketDataService;
    private symbolsService: SymbolsService;
    private registeredRequests: {
        [requestId: string]: {
            request: BloombergMarketDataRequest;
            subscriberIds: string[];
        };
    } = {};

    public constructor(options: WidgetMarketDataRequestManagerOptions) {
        this.logger = buildLogger(
            'WidgetMarketDataRequestManager',
            options.loggingService
        );
        this.marketDataService = options.marketDataService;
        this.symbolsService = options.symbolsService;
    }

    public _store: Store | undefined;

    private get store() {
        if (!this._store) {
            throw new Error('Store has not been set');
        }
        return this._store;
    }

    public register(subscriberId: string, requests: MarketDataRequest[]) {
        if (requests.length > 0) {
            this.logger.info(
                'Registering request for ' + subscriberId,
                requests
            );
        }

        (async () => {
            for (const request of requests) {
                const br = await this.mapToBloombergRequest(request);
                if (br) {
                    const isNewRequest = this.registerRequest(subscriberId, br);
                    if (isNewRequest) {
                        this.marketDataService.subscribe(br);
                    }
                }
            }
        })();

        (async () => {
            const noLongerNeededRequests = await this.deregister(
                subscriberId,
                requests
            );
            for (const noLongerNeededRequest of noLongerNeededRequests) {
                this.marketDataService.unsubscribe(noLongerNeededRequest);
            }
        })();
    }

    public setStore(next: Store) {
        this._store = next;
    }

    private registerRequest(
        subscriberId: string,
        request: BloombergMarketDataRequest
    ): boolean {
        let isNewRequest = false;

        const requestId = requestUid(request);

        if (!this.registeredRequests[requestId]) {
            isNewRequest = true;
            this.registeredRequests[requestId] = {
                request,
                subscriberIds: [subscriberId],
            };
        } else if (
            this.registeredRequests[requestId].subscriberIds.indexOf(
                subscriberId
            ) === -1
        ) {
            this.registeredRequests[requestId].subscriberIds = [
                ...this.registeredRequests[requestId].subscriberIds,
                subscriberId,
            ];
        }

        return isNewRequest;
    }

    private async deregister(
        subscriberId: string,
        subscribedRequests: MarketDataRequest[]
    ): Promise<BloombergMarketDataRequest[]> {
        const noLongerNeededRequests: BloombergMarketDataRequest[] = [];
        const subscribedRequestIds: string[] = [];

        for (const r of subscribedRequests) {
            const br = await this.mapToBloombergRequest(r);
            if (br) {
                subscribedRequestIds.push(requestUid(br));
            }
        }

        const registeredRequestIds = Object.keys(this.registeredRequests);

        for (const registeredRequestId of registeredRequestIds) {
            const registeredRequest =
                this.registeredRequests[registeredRequestId];
            if (
                subscribedRequestIds.indexOf(registeredRequestId) === -1 &&
                registeredRequest.subscriberIds.indexOf(subscriberId) > -1
            ) {
                registeredRequest.subscriberIds =
                    registeredRequest.subscriberIds.filter(
                        (r) => r !== subscriberId
                    );

                this.logger.debug(
                    `Unsubscribing ${subscriberId} from ${registeredRequestId}: ${registeredRequest.subscriberIds.length} subscribers remain`
                );

                if (registeredRequest.subscriberIds.length === 0) {
                    noLongerNeededRequests.push(registeredRequest.request);
                    delete this.registeredRequests[registeredRequestId];
                }
            }
        }

        return noLongerNeededRequests;
    }

    private async mapToBloombergRequest(request: MarketDataRequest) {
        return await mapToBloombergRequest(request, this.symbolsService);
    }
}
